import * as turf from "@turf/turf";
import type { GameClient, Clue, Msg, Question } from "../net";
import { photoUrl } from "../api";
import { openPhotoClue } from "./photoSend";
import { PHOTO_BY_ID } from "../photoTypes";
import { CAT_BY_ID, nearestOf, divisionContaining, fmtDist } from "../landmarks";

// The squad's shared "what we know about the hider" board. Two sources feed it:
//  • AUTO — derived live from the hider's answers to this squad's questions
//    (e.g. "The hider's nearest museum is Rose Center for Earth and Space").
//  • MANUAL — any teammate can type a clue into any category (for answers that
//    came back over text/WhatsApp, or deductions of their own).
// Grouped by question type. Shared squad-wide (never shown to the hider/other teams).

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));

const SECTIONS: { type: string; label: string; emoji: string; photo?: boolean }[] = [
  { type: "matching", label: "Matching", emoji: "🟰" },
  { type: "measuring", label: "Measuring", emoji: "📏" },
  { type: "thermometer", label: "Thermometer", emoji: "🌡" },
  { type: "radar", label: "Radar", emoji: "📡" },
  { type: "photo", label: "Photos", emoji: "📷", photo: true },
];

// Turn an answered question into a plain-English fact about the hider. The corner
// the seeker asked from (`q.place`) is appended so a moving team keeps the context.
function factText(q: Question, answer: string): string {
  const cat = q.catId ? CAT_BY_ID[q.catId] : undefined;
  const catLabel = cat ? cat.label.replace(/ \(.*\)/, "").toLowerCase() : "feature";
  const from = q.place ? ` (asked from ${q.place})` : "";
  if (q.type === "matching" && q.ref) {
    if (cat?.kind === "division") {
      const d = divisionContaining(cat.divId!, q.ref[0], q.ref[1])?.name ?? "—";
      return (answer === "yes" ? `The hider is in the same ${catLabel} as us (${d}).` : `The hider is NOT in our ${catLabel} (${d}).`) + from;
    }
    const n = nearestOf(q.catId!, q.ref[0], q.ref[1]);
    const name = n?.name ?? "—";
    return (answer === "yes" ? `The hider's nearest ${catLabel} is ${name}.` : `The hider's nearest ${catLabel} is NOT ${name}.`) + from;
  }
  if (q.type === "measuring" && q.dist != null) {
    const noun = cat?.kind === "division" ? `${catLabel} border` : `a ${catLabel}`;
    return (answer === "closer"
      ? `The hider is closer than ${fmtDist(q.dist)} to ${noun}.`
      : `The hider is further than ${fmtDist(q.dist)} from ${noun}.`) + from;
  }
  if (q.type === "radar" && q.dist != null) {
    const place = q.place || "our spot";
    return answer === "yes" ? `The hider is within ${fmtDist(q.dist)} of ${place}.` : `The hider is NOT within ${fmtDist(q.dist)} of ${place}.`;
  }
  if (q.type === "thermometer" && q.a && q.b) {
    const d = fmtDist(turf.distance(q.a, q.b, { units: "miles" }));
    const start = q.place ? q.place : "our start";
    return answer === "hotter"
      ? `Moving ${d} from ${start} got us HOTTER — the hider is that way.`
      : `Moving ${d} from ${start} got us COLDER — the hider is the other way.`;
  }
  return "";
}

export interface CluesTab { enter(): void; leave(): void }

export function mountClues(mount: HTMLElement, client: GameClient): CluesTab {
  // Derive auto-clues from the squad thread's answered questions.
  function autoClues(): Clue[] {
    const thread: Msg[] = client.threads[client.seekerKey] || [];
    const answers = new Map<string, string>();
    for (const m of thread) if (m.replyTo && m.answer) answers.set(m.replyTo, m.answer);
    const out: Clue[] = [];
    for (const m of thread) {
      const q = m.question; if (!q || !answers.has(q.id)) continue;
      const text = factText(q, answers.get(q.id)!);
      if (text) out.push({ id: `auto:${q.id}`, type: q.type, text, by: "hider's answer", ts: m.ts, source: "auto" });
    }
    return out;
  }

  // Photos are thread messages from the hider (squad-only). Shown in the Photos
  // section; reply-photos also render inline under their question in the Ask log.
  function photoClues(): Clue[] {
    const thread: Msg[] = client.threads[client.seekerKey] || [];
    const qById = new Map(thread.filter((m) => m.question?.id).map((m) => [m.question!.id, m.question!]));
    return thread.filter((m) => m.photoKey).map((m) => {
      let text = m.text || "";
      const q = m.replyTo ? qById.get(m.replyTo) : undefined; // photo answering a photo-request → show its name
      if (q?.type === "photo" && q.photoType) { const name = PHOTO_BY_ID[q.photoType]?.name; if (name) text = text ? `${name} — ${text}` : name; }
      return { id: `photo:${m.id}`, type: "photo", text, by: m.fromName, ts: m.ts, source: "photo", photoKey: m.photoKey };
    });
  }

  function clueRow(c: Clue): string {
    const auto = c.source === "auto";
    const photo = c.photoKey ? `<img class="clue-photo" src="${photoUrl(c.photoKey)}" loading="lazy" alt="hider photo"/>` : "";
    const src = c.source === "photo" ? `📷 from ${esc(c.by)}` : auto ? "🫥 from the hider's answer" : `✍️ ${esc(c.by)}`;
    return `<div class="clue-row${auto ? " auto" : ""}${c.photoKey ? " photo" : ""}">
      ${photo}
      ${c.text ? `<div class="clue-txt">${esc(c.text)}</div>` : ""}
      <div class="clue-foot"><span class="clue-src">${src}</span>
        ${c.source === "manual" ? `<button class="clue-del" data-cluedel="${esc(c.id)}" title="Remove">remove</button>` : ""}</div>
    </div>`;
  }

  function render() {
    if (mount.querySelector("input:focus")) return; // don't clobber a clue being typed
    const all = [...autoClues(), ...photoClues(), ...(client.clues || [])];
    mount.innerHTML = `
      <div class="clue-head">
        <div class="sk-seclabel">🔍 CLUES — WHAT WE KNOW</div>
        <p class="sk-note">Shared with your squad only. Answers in the app land here automatically; anyone can add clues from texts or hunches.</p>
      </div>
      ${SECTIONS.map((sec) => {
        const items = all.filter((c) => c.type === sec.type).sort((a, b) => a.ts - b.ts);
        return `<section class="clue-sec">
          <div class="clue-sechdr">${sec.emoji} ${sec.label} <span class="sk-on-count">${items.length}</span></div>
          ${items.map(clueRow).join("") || `<div class="clue-empty">No ${sec.label.toLowerCase()} ${sec.photo ? "yet" : "clues yet"}.</div>`}
          ${sec.photo
            ? `<div class="clue-tbd">📷 From the hider's replies — or upload your own.</div>
               <button class="clue-addbtn clue-photo-up" id="clue-photo-upload" style="width:100%">📤 Upload a photo</button>`
            : `<div class="clue-add"><input data-cluetype="${sec.type}" maxlength="280" placeholder="Add a ${sec.label.toLowerCase()} clue…"/><button class="clue-addbtn" data-clueadd="${sec.type}">＋ Add</button></div>`}
        </section>`;
      }).join("")}`;
    wire();
  }
  function addFrom(type: string) {
    const input = mount.querySelector<HTMLInputElement>(`input[data-cluetype="${type}"]`);
    const text = input?.value.trim();
    if (!text) return;
    client.addClue(type, text);
    if (input) input.value = "";
  }
  function wire() {
    mount.querySelectorAll<HTMLButtonElement>("[data-clueadd]").forEach((b) => b.addEventListener("click", () => addFrom(b.dataset.clueadd!)));
    mount.querySelectorAll<HTMLInputElement>("input[data-cluetype]").forEach((i) => i.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") addFrom(i.dataset.cluetype!); }));
    mount.querySelectorAll<HTMLButtonElement>("[data-cluedel]").forEach((b) => b.addEventListener("click", () => client.removeClue(b.dataset.cluedel!)));
    mount.querySelector("#clue-photo-upload")?.addEventListener("click", () => openPhotoClue(client));
  }

  const unsub = client.subscribe(() => { if (mount.offsetParent) render(); });
  return { enter() { render(); }, leave() { unsub?.(); } };
}
